import { useFormikContext } from 'formik'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { Body1, Heading6 } from '@ui/Typography'
import { Radio } from '@ui/components/Radio'

import { isOne } from '@utils/validations'

import { ClientNciPriorityDto } from '@model/ClientNciPriorityDto'

import { createFormField } from '@global/formField/FormField'
import { createFormRadioField } from '@global/formField/RadioGroupField'

import { DocumentManagementNCIFormValues } from './DocumentManagementNCIForm'

const FormRadioGroupField = createFormRadioField<DocumentManagementNCIFormValues>()
const FormField = createFormField<DocumentManagementNCIFormValues>()

interface DocumentNumberFieldFactoryProps {
  handleChooseAnotherDocument?: () => void
  showChooseAnotherDoc?: boolean
}

export const DocumentNumberFieldFactory: React.FC<DocumentNumberFieldFactoryProps> = (props) => {
  const { values } = useFormikContext<DocumentManagementNCIFormValues>()

  return (
    <div className='pt-5'>
      {isOneDocumentType(values.accountNciAvailablePriorities) && (
        <DocumentNumberField {...props} />
      )}
      {isMultipleDocumentTypes(values.accountNciAvailablePriorities) && (
        <DocumentRadioField {...props} />
      )}
    </div>
  )
}

const DocumentNumberField: React.FC<DocumentNumberFieldFactoryProps> = (props) => {
  const { values } = useFormikContext<DocumentManagementNCIFormValues>()
  const { handleChooseAnotherDocument, showChooseAnotherDoc } = props
  const { t } = useTranslation()

  return (
    <React.Fragment>
      <div className='pb-4'>
        <Heading6 medium>{t('Profile.ID-Document:')}</Heading6>
      </div>
      <FormField
        name='nciHistory.nciDocumentNumber'
        label={values.accountNciAvailablePriorities.nciRule.shortName}
        required
      />
      {showChooseAnotherDoc && (
        <>
          <div className='pt-2 pb-2'>
            <Body1 isParagraph>
              {t("Profile.Can't provide")} {values.accountNciAvailablePriorities.nciRule.shortName}?
            </Body1>
          </div>
          <div className='pt-2 pb-2'>
            <Body1 isParagraph>
              <span onClick={handleChooseAnotherDocument} className='is-link'>
                {t('Sign up.Choose another document')}
              </span>
            </Body1>
          </div>
        </>
      )}
    </React.Fragment>
  )
}

const DocumentRadioField: React.FC<DocumentNumberFieldFactoryProps> = (props) => {
  const { values, setFieldValue } = useFormikContext<DocumentManagementNCIFormValues>()
  const { handleChooseAnotherDocument, showChooseAnotherDoc } = props
  const { t } = useTranslation()

  const handleChange = (name: string) => (data: number) => {
    setFieldValue(name, data)
  }

  return (
    <React.Fragment>
      <FormRadioGroupField
        name='nciHistory.documentTypeId'
        value={values.nciHistory.documentTypeId}
      >
        {values.accountNciAvailablePriorities.documentTypes.map((x) => (
          <Radio
            name='nciHistory.documentTypeId'
            value={x.documentTypeId}
            checked={x.documentTypeId === Number(values.nciHistory.documentTypeId)}
            onChange={handleChange('nciHistory.documentTypeId')}
            key={x.name}
          >
            {x.name}
          </Radio>
        ))}
      </FormRadioGroupField>
      <FormField
        name='nciHistory.nciDocumentNumber'
        label={values.accountNciAvailablePriorities.nciRule.shortName}
        required
      />
      {showChooseAnotherDoc && (
        <div className='pt-2 pb-2'>
          <Body1 isParagraph>
            <span onClick={handleChooseAnotherDocument} className='is-link'>
              {t('Sign up.Choose another document')}
            </span>
          </Body1>
        </div>
      )}
    </React.Fragment>
  )
}

const isOneDocumentType = (value: ClientNciPriorityDto) => {
  return isOne(value.documentTypes.length)
}
const isMultipleDocumentTypes = (value: ClientNciPriorityDto) => {
  return value.documentTypes.length > 1
}
